import { ArrowForward } from '@mui/icons-material';
import { Skeleton, Stack } from '@mui/material';
import { Button } from 'system/atoms';
import { VerticalTabs } from 'common/ui';
import { TextBlock } from 'common/ui/TextBlock';
import { extractQueryGetItem } from 'core/utils/apollo/extractGetItem';
import { useGetSourcedCandidateByCandidateProfileQuery } from 'generated-graphql/hooks/apollo-hooks';
import { ItemSeparator } from 'common/ui/ItemSeparator';
import { Paths } from 'services/navigation/paths';
import { useNavigate } from 'react-router-dom';
import { getCandidateProfileInvitationSeparatorItems } from 'modules/candidate-profile/utils/separator-items/getCandidateProfileInvitationSeparatorItems';
import { ConversationDetails } from 'modules/conversations/components/ConversationDetails';
import { useCandidateProfileStore } from 'modules/candidate-profile/stores/useCandidateProfileStore';
import { useEffect, useState } from 'react';
import { EmptyState } from 'system';
import { getAnalysisTitle } from 'utils/getAnalysisTitle';

const CandidateProfileInvitationsSkeleton: React.FC = () => {
  return (
    <Stack gap="30px">
      <Skeleton variant="rectangular" width="500px" height={69} />
      <Stack
        direction="row"
        justifyContent="space-between"
        gap="10px"
        width="100%"
      >
        <Skeleton variant="rectangular" width="500px" height={32} />
        <Skeleton variant="rectangular" width="150px" height={32} />
      </Stack>
    </Stack>
  );
};

export const CandidateProfileInvitationsTab: React.FC = () => {
  const navigate = useNavigate();
  const { sourcedCandidates } = useCandidateProfileStore();
  const [sourcedCandidateId, setSourcedCandidateId] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (!sourcedCandidates) return;
    if (sourcedCandidates?.[0]?.id) {
      setSourcedCandidateId(sourcedCandidates?.[0]?.id);
    }
  }, [sourcedCandidates]);

  const { data } = useGetSourcedCandidateByCandidateProfileQuery({
    variables: {
      candidateId: sourcedCandidateId ?? ''
    },
    skip: !sourcedCandidateId
  });

  const sourcedCandidate = extractQueryGetItem(data);

  const jobDescription = sourcedCandidate?.candidateBatch?.jobDescriptionParsed;

  const separatorItems =
    getCandidateProfileInvitationSeparatorItems(sourcedCandidate);

  if (sourcedCandidates?.length === 0) {
    return <EmptyState type="no-records" />;
  }

  const selectedIndex =
    sourcedCandidates?.findIndex(
      (invitation) => invitation.id === sourcedCandidateId
    ) ?? 0;

  return (
    <Stack direction="row">
      <VerticalTabs
        containerSx={{
          height: 'fit-content'
        }}
        defaultSelectedIndex={selectedIndex >= 0 ? selectedIndex : 0}
        items={sourcedCandidates?.map((invitation) => ({
          id: invitation.id,
          title: getAnalysisTitle(invitation.candidateBatch),
          subtitle:
            invitation.candidateBatch?.jobDescriptionParsed?.employerName ?? '',
          description: invitation.location
        }))}
        onTabChange={(item) => {
          setSourcedCandidateId(item.id);
        }}
      />
      <Stack flex={1} gap="30px">
        {sourcedCandidateId && !sourcedCandidate && (
          <CandidateProfileInvitationsSkeleton />
        )}
        {sourcedCandidate && (
          <>
            <TextBlock
              variant="section"
              title={getAnalysisTitle(sourcedCandidate?.candidateBatch)}
              subtitle={jobDescription?.location ?? ''}
            />
            <Stack direction="row" justifyContent="space-between">
              <ItemSeparator items={separatorItems} />
              <Button
                variant="tertiary"
                sx={{ padding: 0 }}
                onClick={() => {
                  navigate(
                    `${Paths.ANALYSE}/${sourcedCandidate.candidateBatch?.id}?tab=Source_candidates&sourcedCandidateId=${sourcedCandidate.id}`
                  );
                }}
              >
                View analysis <ArrowForward />
              </Button>
            </Stack>
            {sourcedCandidate.conversationId && (
              <ConversationDetails
                conversationId={sourcedCandidate.conversationId}
                shouldHideApplicationStage
                shouldHideCandidateDetails
                shouldHideScreeningInProgress
              />
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
};
